import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Typography,
  useMediaQuery,
  Alert } from '@mui/material';
import { makeStyles } from '@mui/styles';
import SelectField from 'components/select-field';
import React, { FC, useEffect } from 'react';
import { documentCalculation, formDocumentCalculation, pricePagesName } from './services';
import { Controller, UseFormReturn } from 'react-hook-form';
import NumberField from 'components/number-field';
import { useTranslation } from 'react-i18next';
import TextField from 'components/text-field';
import SaveButton from 'components/common/save-button';
import { formatMoney } from 'utils/helpers';

const useStyles = makeStyles((theme: any) => ({
  container: { padding: theme.spacing(3) },
  form: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  removecontentPadding: { '& .MuiDialogContent-root': { padding: 0 } },
  nocontendPadding: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  text: {
    textTransform: 'none',
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
  itempaddinfoother: {
    '& > * ': {
      marginLeft: theme.spacing(2),
    },
  },
  itempaddinType: {
    '& > * ': { marginTop: theme.spacing(2) },
    padding: theme.spacing(3),
    margin: 0,
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2),
    },
  },
  errorIconContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  priceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: theme.spacing(2),
  },
  priceLabel: {
    color: 'black',
  },
  priceValue: {
    color: 'black',
    fontFamily: 'monospace',
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  numericField: {
    '& .MuiInputBase-input': {
      fontFamily: 'monospace',
      fontVariantNumeric: 'tabular-nums',
      textAlign: 'right',
    },
  },
}));

type documentCalculationFees = {
  open: boolean;
  onClose: () => void;
  methods: UseFormReturn<formDocumentCalculation>;
  loading: boolean;
  handleEntry: (e?: React.BaseSyntheticEvent) => Promise<void>;
  errors: string[];
  isEditing: boolean;
};

const DocumentModalCalculationScene: FC<documentCalculationFees> = ({
  open,
  onClose,
  methods,
  handleEntry,
  loading,
  errors,
  isEditing,
}) => {
  const { watch, setValue, control } = methods;
  const [CurrentTDI, numberName, numberPages] = watch([
    'currentTDI',
    'numberOfNames',
    'numberOfPages',
  ]);
  const totalPrices = pricePagesName(CurrentTDI, numberName, numberPages);
  const classes = useStyles();
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('md'));

  useEffect(() => {
    setValue('amount', totalPrices.total);
  }, [totalPrices.total, setValue]);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      fullWidth
      fullScreen={isMobile}
      className={classes.removecontentPadding}
    >
      <form className={classes.form} onSubmit={handleEntry}>
        <DialogContent className={classes.nocontendPadding}>
          <Grid container className={classes.itempaddinType} direction="column">
            <Grid item xs={12}>
              <Typography variant="h6" style={{ color: 'black' }}>
                {isEditing ? t('charges:edit-document') : t('charges:add-document')}
              </Typography>
            </Grid>
            {!!errors.length && (
              <Grid item xs={12}>
                <Alert severity="error" classes={{ icon: classes.errorIconContainer }}>
                  {errors.map((error) => (
                    <Typography variant="subtitle1">{error}</Typography>
                  ))}
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <Grid container direction="row" spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Grid container direction="column" spacing={2}>
                    <Grid item xs={12}>
                      <Controller
                        control={control}
                        name="currentTDI"
                        render={({ field }) => (
                          <SelectField
                            value={field.value?.countyName || ''}
                            data={documentCalculation}
                            dataKey="countyName"
                            dataValue="countyName"
                            dataText={['tdiCountyCode', 'countyName']}
                            label={t('common:select-county-tdi')}
                            handleChange={(event) => {
                              const currentCalculation = documentCalculation.find(
                                (item) => item.countyName === event.target.value
                              );
                              field.onChange(currentCalculation);
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        control={control}
                        name="numberOfPages"
                        render={({ field: { ref, onChange, ...field } }) => (
                          <NumberField
                            {...field}
                            customRef={ref}
                            className={classes.numericField}
                            onValueChange={(e) => {
                              onChange(e.floatValue);
                            }}
                            onChange={undefined}
                            label={t('common:number-of-pages')}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        control={control}
                        name="numberOfNames"
                        render={({ field: { ref, onChange, ...field } }) => (
                          <NumberField
                            {...field}
                            customRef={ref}
                            className={classes.numericField}
                            onValueChange={(e) => {
                              onChange(e.floatValue);
                            }}
                            onChange={undefined}
                            label={t('common:number-of-names')}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Grid container direction="column">
                    <Grid item xs={12}>
                      <Typography variant="body1" style={{ color: 'black', marginBottom: 4 }}>
                        {t('charges:pricesOfTDISelected')}
                      </Typography>
                      {[
                        { label: t('charges:priceOfFirstPage'), value: totalPrices.firstPage, isPrice: true },
                        { label: t('charges:pricePerPage'), value: CurrentTDI?.eachAdditionalPage || 0, isPrice: true },
                        { label: t('charges:extraPages'), value: numberPages > 0 ? numberPages - 1 : 0, isPrice: false },
                        { label: t('charges:priceOfAdditionalPages'), value: totalPrices.additionalPages, isPrice: true },
                        { label: t('charges:namesIncluded'), value: CurrentTDI?.namesIncluded || 0, isPrice: false },
                        { label: t('charges:pricePerExtraName'), value: CurrentTDI?.eachAdditionalName || 0, isPrice: true },
                        { label: t('charges:priceOfAdditionalNames'), value: totalPrices.additionalNames, isPrice: true },
                      ].map((row) => (
                        <Box key={row.label} className={classes.priceRow}>
                          <Typography variant="body2" className={classes.priceLabel}>
                            {row.label}
                          </Typography>
                          <Typography variant="body2" className={classes.priceValue}>
                            {row.isPrice ? formatMoney(row.value) : row.value}
                          </Typography>
                        </Box>
                      ))}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="amount"
                control={control}
                render={({ field: { ref, onChange, ...field } }) => (
                  <NumberField
                    {...field}
                    customRef={ref}
                    className={classes.numericField}
                    prefix="$"
                    thousandSeparator
                    fixedDecimalScale
                    onValueChange={(values) => {
                      onChange(values.floatValue);
                    }}
                    onChange={undefined}
                    label={t('accounting:amount')}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <TextField {...field} inputRef={ref} label={t('accounting:description')} />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions disableSpacing style={{ padding: 16 }} className={classes.itempaddinfoother}>
          <Button
            color="primary"
            variant="outlined"
            disableElevation
            disabled={loading}
            onClick={onClose}
          >
            <Typography variant="body2" className={classes.text}>
              {t('common:close')}
            </Typography>
          </Button>
          <SaveButton type="submit" customText={t('accounting:add-entry')} loading={loading} />
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default DocumentModalCalculationScene;
